import React from 'react';
import { preventiveTaskFullInformationPropTypes } from '../proptypes/PreventiveTaskPropTypes';
import {
  ACTIVITY_ACTION,
  PREVENTIVE_TASK_MONTHLY_TYPE,
  PREVENTIVE_TASK_PRIORITY,
  PREVENTIVE_TASK_REPEAT_TYPE,
  PREVENTIVE_TASK_WEEK_ORDER,
  PREVENTIVE_TASK_WEEKDAY,
} from '../../../../../config/constants';
import ResourceProperty from '../../../../common/resource/ResourceProperty';
import EnumStatus from '../../../../common/EnumStatus';
import IsActive from '../../../../common/cells/IsActive';

const getEnumLabelByValue = (enumObject, value) => {
  if (value === null || value === undefined) return '-';

  const enumItem = Object.keys(enumObject)
    .map((key) => enumObject[key])
    .find((item) => item.value === value);

  if (!enumItem) return value;

  return enumItem.label || value;
};

const getEnumCodeByValue = (enumObject, value) => {
  if (value === null || value === undefined) return null;

  const enumItem = Object.keys(enumObject)
    .map((key) => enumObject[key])
    .find((item) => item.value === value);

  if (!enumItem) return null;

  return enumItem.code;
};

const formatWeekdays = (weekdays) => {
  if (!Array.isArray(weekdays) || !weekdays.length) return '-';

  return weekdays
    .map(({ weekday }) => getEnumLabelByValue(PREVENTIVE_TASK_WEEKDAY, weekday))
    .join(', ');
};

const formatMonthlyDays = (monthlyDays) => {
  if (!Array.isArray(monthlyDays) || !monthlyDays.length) return '-';

  return monthlyDays
    .map((monthlyDay) => {
      const isLastDay =
        monthlyDay.lastDayOfMonth ||
        monthlyDay.isLastDayOfMonth ||
        monthlyDay.lastDayOfMonth === 1 ||
        monthlyDay.isLastDayOfMonth === 1;

      if (isLastDay) return 'Último día del mes';

      return monthlyDay.dayOfMonth
        ? `Día ${monthlyDay.dayOfMonth} del mes`
        : '-';
    })
    .join(', ');
};

const formatMonthlyRules = (monthlyRules) => {
  if (!Array.isArray(monthlyRules) || !monthlyRules.length) return '-';

  return monthlyRules
    .map((monthlyRule) => {
      const weekdayLabel = getEnumLabelByValue(
        PREVENTIVE_TASK_WEEKDAY,
        monthlyRule.weekday,
      );

      const monthlyTypeCode =
        typeof monthlyRule.monthlyType === 'number'
          ? getEnumCodeByValue(
              PREVENTIVE_TASK_MONTHLY_TYPE,
              monthlyRule.monthlyType,
            )
          : monthlyRule.monthlyType;

      if (
        monthlyTypeCode ===
        PREVENTIVE_TASK_MONTHLY_TYPE.LAST_WEEKDAY_OF_MONTH.code
      ) {
        return `Último ${weekdayLabel} del mes`;
      }

      const weekOrderLabel = getEnumLabelByValue(
        PREVENTIVE_TASK_WEEK_ORDER,
        monthlyRule.weekOrder,
      );

      return `${weekOrderLabel} ${weekdayLabel} del mes`;
    })
    .join(', ');
};

const PreventiveTaskBasicInformationResource = ({
  name,
  companyPreventiveTaskId,
  description,
  actionType,
  active,
  priority,
  deviation,
  repeatType,
  repeatInterval,
  weekdays,
  monthlyDays,
  monthlyRules,
}) => (
  <>
    <ResourceProperty label="Código de la Tarea:">
      {companyPreventiveTaskId || '-'}
    </ResourceProperty>

    <ResourceProperty label="Nombre de la Tarea:">
      {name || '-'}
    </ResourceProperty>

    <ResourceProperty label="Descripción:">
      {description || '-'}
    </ResourceProperty>

    <ResourceProperty label="Tipo de Acción:">
      <EnumStatus enumObject={ACTIVITY_ACTION} enumValue={actionType} />
    </ResourceProperty>

    <ResourceProperty label="Estado:">
      <IsActive value={active} />
    </ResourceProperty>

    <ResourceProperty label="Prioridad:">
      <EnumStatus enumObject={PREVENTIVE_TASK_PRIORITY} enumValue={priority} />
    </ResourceProperty>

    <ResourceProperty label="Notificación Programada:">
      {deviation !== null && deviation !== undefined
        ? `${deviation} día(s)`
        : '-'}
    </ResourceProperty>

    <ResourceProperty label="Tipo de Frecuencia:">
      <EnumStatus
        enumObject={PREVENTIVE_TASK_REPEAT_TYPE}
        enumValue={repeatType}
      />
    </ResourceProperty>

    {repeatType === PREVENTIVE_TASK_REPEAT_TYPE.DAYS.code && (
      <ResourceProperty label="Configuración:">
        Cada {repeatInterval} día(s)
      </ResourceProperty>
    )}

    {repeatType === PREVENTIVE_TASK_REPEAT_TYPE.WEEKLY.code && (
      <>
        <ResourceProperty label="Configuración:">
          Cada {repeatInterval} semana(s)
        </ResourceProperty>

        <ResourceProperty label="Días de la semana:">
          {formatWeekdays(weekdays)}
        </ResourceProperty>
      </>
    )}

    {repeatType === PREVENTIVE_TASK_REPEAT_TYPE.MONTHLY_DAYS.code && (
      <ResourceProperty label="Días del mes:">
        {formatMonthlyDays(monthlyDays)}
      </ResourceProperty>
    )}

    {repeatType === PREVENTIVE_TASK_REPEAT_TYPE.MONTHLY_RULE.code && (
      <ResourceProperty label="Regla mensual:">
        {formatMonthlyRules(monthlyRules)}
      </ResourceProperty>
    )}
  </>
);

PreventiveTaskBasicInformationResource.propTypes =
  preventiveTaskFullInformationPropTypes;

export default PreventiveTaskBasicInformationResource;
